import { computed, ref } from 'vue';
import { ofetch } from 'ofetch';
import { cancelSocialLogin } from '~/composables/socialAuth';
import {
  defineNuxtPlugin,
  refreshCookie,
  useCookie,
  useRequestHeaders,
  useRuntimeConfig
} from '#imports';
import {
  clearApiCache,
  hasAuthenticatedUserHint,
  isAuthRequiredUserRequest
} from '~/composables/useLocalizedFetch';

const PUBLIC_AUTH_COOKIE = 'idmanify_user';
const AUTH_SYNC_KEY = 'idmanify_auth_sync';

export default defineNuxtPlugin(async (nuxtApp) => {
  // Skip plugin when rendering error page
  if (nuxtApp.payload?.error) {
    return {};
  }

  const config = useRuntimeConfig();
  const apiBase = String(config.public.API_URL || '').replace(/\/$/, '');
  const serverRequestHeaders = import.meta.server
    ? useRequestHeaders(['cookie'])
    : {};

  /**
   * Auth architecture for the web frontend:
   * - Laravel Sanctum owns the real login state through its session cookies.
   * - The browser must authenticate with `credentials: 'include'`, not with a
   *   Bearer token stored in JavaScript-readable cookies/localStorage.
   * - `idmanify_user` is only a non-sensitive UI mirror so existing components
   *   can react quickly. It must never be treated as proof of authentication.
   */
  const session = ref(null);
  const loggedIn = computed(() => !!session.value?.email);

  const publicUserCookie = useCookie(PUBLIC_AUTH_COOKIE, {
    path: '/',
    sameSite: 'strict',
    maxAge: 60 * 60 * 24 * 365
  });
  const xsrfCookie = useCookie('XSRF-TOKEN');

  const sanitizeUser = (user) => {
    if (!user || typeof user !== 'object') return null;

    const safeUser = { ...user };
    delete safeUser.token;
    delete safeUser.access_token;
    delete safeUser.plain_text_token;

    return safeUser?.email ? safeUser : null;
  };

  const persistPublicUser = (user) => {
    const safeUser = sanitizeUser(user);
    publicUserCookie.value = safeUser;
    session.value = safeUser;
    return safeUser;
  };

  const clearLocalAuthState = () => {
    session.value = null;
    publicUserCookie.value = null;
    clearApiCache(nuxtApp);
  };

  const notifyAuthChanged = () => {
    if (!import.meta.client) return;
    localStorage.setItem(AUTH_SYNC_KEY, String(Date.now()));
  };

  const authSignature = (value = session.value) => {
    if (!value?.email) return 'guest';
    return String(value.id || value.guid || value.email);
  };

  const requestHeaders = () => {
    const headers = {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };

    if (import.meta.server) {
      Object.assign(headers, serverRequestHeaders);
      headers.referer = config.public.FRONTEND_URL;
    }

    return headers;
  };

  const xsrfHeader = () => {
    const token = xsrfCookie.value;
    return token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : {};
  };

  let sanctumFetch = null;

  const fetchCsrfCookie = async () => {
    await $fetch(`${apiBase}/api/auth/csrf-cookie`, {
      method: 'GET',
      credentials: 'include',
      headers: requestHeaders()
    });

    if (import.meta.client) refreshCookie('XSRF-TOKEN');
  };

  const fetchUser = async ({ force = true } = {}) => {
    if (!force && !hasAuthenticatedUserHint(serverRequestHeaders)) {
      clearLocalAuthState();
      return false;
    }

    try {
      const response = await (sanctumFetch || $fetch)(
        `${apiBase}/api/auth/user`,
        {
          method: 'GET',
          credentials: 'include',
          headers: requestHeaders()
        }
      );

      const user = response?.data || response;
      if (!user?.email) {
        clearLocalAuthState();
        return false;
      }

      persistPublicUser(user);
      return true;
    } catch {
      clearLocalAuthState();
      return false;
    }
  };

  const login = async (credentials) => {
    await fetchCsrfCookie();

    const response = await $fetch(`${apiBase}/api/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...requestHeaders(),
        ...xsrfHeader(),
        'Content-Type': 'application/json'
      },
      body: credentials
    });

    await fetchUser({ force: true });
    clearApiCache(nuxtApp);
    notifyAuthChanged();

    return response;
  };

  const logout = async () => {
    cancelSocialLogin();
    try {
      await fetchCsrfCookie();
      await $fetch(`${apiBase}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...requestHeaders(),
          ...xsrfHeader()
        }
      });
    } finally {
      clearLocalAuthState();
      notifyAuthChanged();
    }
  };

  const syncSessionFromBrowser = async () => {
    const before = authSignature();
    await fetchUser({ force: false });
    const after = authSignature();

    if (before !== after) clearApiCache(nuxtApp);
    return before !== after;
  };

  if (import.meta.client) {
    let syncPromise = null;
    let lastSyncAt = 0;

    const scheduleSessionSync = () => {
      const now = Date.now();
      if (syncPromise || now - lastSyncAt < 750) return syncPromise;

      lastSyncAt = now;
      syncPromise = syncSessionFromBrowser().finally(() => {
        syncPromise = null;
      });

      return syncPromise;
    };

    window.addEventListener('focus', scheduleSessionSync);
    window.addEventListener('pageshow', scheduleSessionSync);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') scheduleSessionSync();
    });
    window.addEventListener('storage', (event) => {
      if (event.key === AUTH_SYNC_KEY) scheduleSessionSync();
    });
  }

  /**
   * Keep legacy direct `$fetch` calls working with Sanctum cookies. We only add
   * credentials and loader handling here; Authorization/Bearer headers are
   * intentionally not generated for the web frontend.
   */
  sanctumFetch = ofetch.create({
    async onRequest(context) {
      context.options.credentials = 'include';

      if (String(context.request).includes('csrf-cookie')) {
        nuxtApp.$loader?.startProgress();
      }

      return context;
    },

    onRequestError() {
      nuxtApp.$loader?.stopProgress();
    },

    onResponse() {
      nuxtApp.$loader?.stopProgress();
    },

    onResponseError() {
      nuxtApp.$loader?.stopProgress();
    }
  });

  const guardedFetch = (request, options = {}) => {
    const target = String(request || options.baseURL || '');

    if (
      isAuthRequiredUserRequest(target, apiBase) &&
      !hasAuthenticatedUserHint(serverRequestHeaders)
    ) {
      return Promise.resolve(null);
    }

    return sanctumFetch(request, options);
  };

  Object.assign(guardedFetch, sanctumFetch);
  // Nitro's server $fetch dispatches relative i18n URLs internally. Replacing
  // it with browser ofetch breaks translations on later SSR requests and shares
  // a request-scoped auth closure across users. Keep the legacy wrapper client-only.
  if (import.meta.client) globalThis.$fetch = guardedFetch;

  const initialPublicUser = sanitizeUser(publicUserCookie.value);
  if (initialPublicUser) session.value = initialPublicUser;

  await fetchUser({ force: false });

  const authApi = {
    loggedIn,
    session,
    login,
    logout,
    updateSession: fetchUser,
    fetchUser
  };

  if (import.meta.client) globalThis.__IDMANIFY_AUTH__ = authApi;

  return {
    provide: {
      auth: authApi
    }
  };
});
